/*
 * Client-side decode of the admin-namespace JWT (flows/admin-auth). RBAC is stateless: the
 * operator's permissions are baked into the token at login, so the console reads them straight from
 * the claims to drive nav visibility and the must-reset gate — no /me round-trip. This is a decode,
 * not a verification: the backend's requirePermission guard is the security boundary (the token is
 * signed and re-checked on every admin route); a forged client-side claim only changes what the UI
 * offers, and every guarded route refuses it anyway.
 */

export interface AdminClaims {
  /** admins.id */
  readonly sub: string;
  /** e.g. SUPER_ADMIN */
  readonly role: string;
  /** The full permission grant resolved from the role at login. */
  readonly permissions: readonly string[];
  /** The forced-reset gate: true on the bootstrap account and after any admin-triggered reset. */
  readonly mustResetPw: boolean;
}

function decodeBase64Url(segment: string): string | null {
  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    return atob(padded);
  } catch {
    return null;
  }
}

/**
 * Decode the claims the console needs from an admin token, or null if the token is malformed or
 * missing the expected claim shape. Never throws — a bad token is treated as no session.
 */
export function decodeAdminClaims(token: string): AdminClaims | null {
  const segments = token.split('.');
  if (segments.length !== 3) {
    return null;
  }
  const json = decodeBase64Url(segments[1] ?? '');
  if (json === null) {
    return null;
  }

  let payload: unknown;
  try {
    payload = JSON.parse(json);
  } catch {
    return null;
  }
  if (typeof payload !== 'object' || payload === null) {
    return null;
  }

  const claims = payload as Record<string, unknown>;
  if (
    typeof claims.sub !== 'string' ||
    typeof claims.role !== 'string' ||
    typeof claims.mustResetPw !== 'boolean' ||
    !Array.isArray(claims.permissions) ||
    !claims.permissions.every((p): p is string => typeof p === 'string')
  ) {
    return null;
  }

  return {
    sub: claims.sub,
    role: claims.role,
    permissions: claims.permissions,
    mustResetPw: claims.mustResetPw,
  };
}
